"use client";

// Bandeau de lancement d'Hermes Agent, en tête de l'onglet Découvrir de
// /courses.
//
// Il ne reprend pas l'argument de la mémoire (déjà mis en scène sur la fiche
// par HaMemory) mais celui que la carte du catalogue ne peut pas prouver en
// trois lignes : l'agent travaille sans vous. La maquette de droite ne montre
// donc pas un résultat, elle le fabrique sous les yeux du visiteur. La nuit
// se rejoue en boucle : l'agent écrit, le message tombe, la tâche se coche,
// l'horloge avance de 3 h 07 à 3 h 12, et l'étiquette « Vous dormiez » arrive
// en dernier, une fois que tout est fait.
//
// Trois précautions pour que la boucle reste supportable :
// - elle ne tourne que si le bandeau est à l'écran (IntersectionObserver) ;
// - elle s'arrête net sur son état final si le visiteur a demandé moins
//   d'animations ;
// - le corps de la maquette a une hauteur réservée, sinon le cadre sauterait
//   à chaque étape.
//
// Palette indigo/violet, celle des blocs Ha* de la fiche, pour que le visiteur
// qui clique retrouve le même univers. Le bandeau est sombre dans les deux
// thèmes : les couleurs de texte sont donc explicites plutôt que doublées
// en dark:.
//
// C'est le seul grand bloc de l'onglet Découvrir. Tout ce qui suit (bande des
// formations récentes) reste volontairement en petit format.

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { getCourseBySlug } from "@/lib/courses-catalog";
import { formatCourseDuration } from "@/lib/courseDuration";

const SLUG = "formation-hermes-agent";

// Durée de chaque palier de la scène, en millisecondes. Le dernier est la
// pause pendant laquelle le visiteur lit le résultat avant que tout reparte.
const BEATS = [1600, 1300, 1100, 4200];
const LAST_BEAT = BEATS.length - 1;

export default function HermesLaunchBanner() {
    const t = useTranslations();
    const reduceMotion = useReducedMotion();

    // La durée vient du catalogue : le bandeau ne peut pas se désynchroniser
    // de la carte le jour où la formation s'allonge.
    const durationLabel = formatCourseDuration(getCourseBySlug(SLUG), t);

    const [beat, setBeat] = useState(0);
    const [onScreen, setOnScreen] = useState(false);
    const sceneRef = useRef(null);

    // Rien ne tourne dans un bandeau que personne ne regarde.
    useEffect(() => {
        const node = sceneRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            setOnScreen(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setOnScreen(entry.isIntersecting),
            { threshold: 0.3 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // useReducedMotion vaut null au premier rendu : plutôt que de corriger
    // l'état après coup, la scène affichée est dérivée. Sans animation, elle
    // se rend directement dans son état final.
    const shownBeat = reduceMotion ? LAST_BEAT : beat;

    useEffect(() => {
        if (reduceMotion || !onScreen) return undefined;
        const id = setTimeout(
            () => setBeat((current) => (current + 1) % BEATS.length),
            BEATS[beat],
        );
        return () => clearTimeout(id);
    }, [beat, onScreen, reduceMotion]);

    // Entrée du bandeau : fondu + 10px, la maquette juste après le texte.
    const appear = (delay = 0) =>
        reduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 10 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.25, ease: "easeOut", delay },
              };

    // Les éléments de la scène partagent la même entrée, plus courte.
    const enter = {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, transition: { duration: 0.2 } },
        transition: { duration: 0.28, ease: "easeOut" },
    };

    const timeLabel = t(
        shownBeat >= 1
            ? "HermesLaunchBanner_mockup_time"
            : "HermesLaunchBanner_mockup_time_before",
    );

    return (
        <Link href={`/course/${SLUG}`} className="group block mt-5">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0e1424] via-[#111a2e] to-[#0a0f1f] ring-1 ring-white/5">
                <div className="absolute -top-28 -right-24 w-[440px] h-[440px] bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-28 -left-20 w-[340px] h-[340px] bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative grid lg:grid-cols-[1fr_1.1fr] gap-0 items-stretch">
                    {/* GAUCHE — la promesse */}
                    <motion.div
                        {...appear()}
                        className="p-6 sm:p-9 md:p-11 flex flex-col justify-center"
                    >
                        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30 px-3 py-1 mb-5">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400" />
                            </span>
                            <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-300">
                                {t("HermesLaunchBanner_eyebrow")}
                            </span>
                        </div>

                        <h2 className="text-2xl sm:text-3xl md:text-[2.2rem] font-bold text-white leading-[1.1] tracking-tight mb-3">
                            {t.rich("HermesLaunchBanner_title", {
                                highlight: (chunks) => (
                                    <span className="bg-gradient-to-r from-indigo-300 to-violet-200 bg-clip-text text-transparent">
                                        {chunks}
                                    </span>
                                ),
                            })}
                        </h2>

                        <p className="text-gray-400 text-[14px] sm:text-base mb-6 leading-relaxed">
                            {t.rich("HermesLaunchBanner_description", {
                                duration: durationLabel,
                                strong: (chunks) => (
                                    <strong className="text-white font-semibold">
                                        {chunks}
                                    </strong>
                                ),
                            })}
                        </p>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <span className="inline-flex items-center justify-center gap-2 bg-white !text-gray-900 hover:bg-gray-100 text-sm font-semibold px-5 py-3 rounded-xl transition-all">
                                {t("HermesLaunchBanner_cta")}
                                <SolarIcon
                                    name="arrow-right-linear"
                                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                                />
                            </span>
                            <span className="text-xs text-gray-500">
                                {t("HermesLaunchBanner_meta", {
                                    duration: durationLabel,
                                })}
                            </span>
                        </div>
                    </motion.div>

                    {/* DROITE — la nuit où il a travaillé sans vous */}
                    <motion.div
                        {...appear(0.05)}
                        ref={sceneRef}
                        className="relative px-6 pb-8 pt-2 sm:p-8 lg:p-10 lg:pl-0 flex items-center justify-center lg:min-h-[280px]"
                    >
                        {/* Halo qui respire derrière la maquette : la seule
                            animation continue du bandeau, assez lente pour ne
                            jamais attirer l'œil. */}
                        {!reduceMotion && (
                            <motion.div
                                aria-hidden
                                className="absolute inset-8 rounded-3xl bg-indigo-500/20 blur-3xl pointer-events-none"
                                animate={{ opacity: [0.4, 0.75, 0.4] }}
                                transition={{
                                    duration: 6,
                                    ease: "easeInOut",
                                    repeat: Infinity,
                                }}
                            />
                        )}

                        <div className="relative w-full max-w-[460px]">
                            <div className="relative rounded-xl bg-[#0a0e1a] ring-1 ring-white/10 shadow-2xl overflow-hidden">
                                {/* Balayage de lumière au moment où la tâche
                                    se termine. Une fois par boucle, 700 ms. */}
                                <AnimatePresence>
                                    {shownBeat === 2 && !reduceMotion && (
                                        <motion.div
                                            aria-hidden
                                            className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-indigo-300/10 to-transparent pointer-events-none"
                                            initial={{ x: "-120%" }}
                                            animate={{ x: "220%" }}
                                            exit={{ opacity: 0 }}
                                            transition={{
                                                duration: 0.7,
                                                ease: "easeInOut",
                                            }}
                                        />
                                    )}
                                </AnimatePresence>

                                <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-[#0f1422] border-b border-white/5">
                                    <Image
                                        src="/images/site/courses/icon-hermesagent.png"
                                        alt=""
                                        width={22}
                                        height={22}
                                        className="rounded-md flex-shrink-0"
                                    />
                                    <span className="text-[12px] font-semibold text-white">
                                        {t("HermesLaunchBanner_mockup_name")}
                                    </span>
                                    <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-indigo-300">
                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                        {t("HermesLaunchBanner_mockup_status")}
                                    </span>
                                    <span className="ml-auto flex items-center gap-1.5 text-[10px] text-gray-500 font-mono">
                                        <SolarIcon
                                            name="clock-circle-linear"
                                            className="w-3 h-3"
                                        />
                                        <motion.span
                                            key={timeLabel}
                                            initial={
                                                reduceMotion
                                                    ? false
                                                    : { opacity: 0, y: -4 }
                                            }
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {timeLabel}
                                        </motion.span>
                                    </span>
                                </div>

                                {/* Hauteur réservée : le cadre ne bouge pas
                                    pendant que la scène se remplit. */}
                                <div className="p-4 min-h-[132px]">
                                    <AnimatePresence>
                                        {shownBeat === 0 && (
                                            <motion.div
                                                key="typing"
                                                {...enter}
                                                className="flex items-center gap-2.5"
                                            >
                                                <span className="flex items-center gap-1 rounded-2xl rounded-tl-md bg-white/[0.06] ring-1 ring-white/10 px-3 py-2.5">
                                                    {[0, 1, 2].map((dot) => (
                                                        <motion.span
                                                            key={dot}
                                                            className="w-1.5 h-1.5 rounded-full bg-indigo-300"
                                                            animate={{
                                                                opacity: [
                                                                    0.25, 1,
                                                                    0.25,
                                                                ],
                                                            }}
                                                            transition={{
                                                                duration: 1.2,
                                                                ease: "easeInOut",
                                                                repeat: Infinity,
                                                                delay:
                                                                    dot * 0.15,
                                                            }}
                                                        />
                                                    ))}
                                                </span>
                                                <p className="text-[11px] text-gray-500 mb-0">
                                                    {t(
                                                        "HermesLaunchBanner_mockup_typing",
                                                    )}
                                                </p>
                                            </motion.div>
                                        )}

                                        {shownBeat >= 1 && (
                                            <motion.div
                                                key="message"
                                                {...enter}
                                                className="max-w-[300px] rounded-2xl rounded-tl-md bg-white/[0.06] ring-1 ring-white/10 px-3.5 py-2.5"
                                            >
                                                <p className="text-[12px] sm:text-[13px] text-gray-200 leading-relaxed mb-0">
                                                    {t(
                                                        "HermesLaunchBanner_mockup_message",
                                                    )}
                                                </p>
                                            </motion.div>
                                        )}

                                        {shownBeat >= 2 && (
                                            <motion.div
                                                key="done"
                                                {...enter}
                                                className="mt-3 flex items-center gap-2 pl-1"
                                            >
                                                <motion.span
                                                    initial={
                                                        reduceMotion
                                                            ? false
                                                            : { scale: 0.7 }
                                                    }
                                                    animate={{ scale: 1 }}
                                                    transition={{
                                                        duration: 0.25,
                                                        ease: "easeOut",
                                                    }}
                                                    className="flex-shrink-0"
                                                >
                                                    <SolarIcon
                                                        name="check-circle-bold"
                                                        className="w-4 h-4 text-emerald-400"
                                                    />
                                                </motion.span>
                                                <p className="text-[11px] text-gray-500 mb-0">
                                                    {t(
                                                        "HermesLaunchBanner_mockup_done",
                                                    )}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <AnimatePresence>
                                {shownBeat >= 3 && (
                                    <motion.div
                                        key="footnote"
                                        {...enter}
                                        className="mt-4 ml-auto w-fit rounded-xl bg-indigo-500/15 ring-1 ring-indigo-400/30 px-3.5 py-2 lg:mt-0 lg:ml-0 lg:absolute lg:-bottom-4 lg:-right-4 lg:rotate-1"
                                    >
                                        <p className="text-[12px] font-semibold text-indigo-200 mb-0">
                                            {t(
                                                "HermesLaunchBanner_mockup_footnote",
                                            )}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            </div>
        </Link>
    );
}
